import * as React from "react";

import {ArrowPathIcon} from "@heroicons/react/24/outline";

import * as habits from "@client/features/habits";
import * as healthkit from "@client/features/healthkit";
import {notify} from "@client/features/notifications";
import * as orm from "@client/features/orm";

import * as typography from "@client/shared/typography";

import * as hardware from "@client/hardware";
import {useAppDispatch, useAppSelector} from "@client/store";

// A fully self-contained journal card (JournalHabitShell, like the MyFitnessPal
// card) that re-syncs the user's entire Apple Health history. It owns its own
// visibility: it reads the profile straight from redux
// (useAppSelector) and only renders when NEEDS_APPLE_HEALTH_RESYNC is set and
// RESYNCED_APPLE_HEALTH is not, so callers can render it unconditionally (inside a
// Suspense boundary — it suspends on the memoized native earliest-sample lookup).
// Because it subscribes to the store directly, dismissAnnouncement's profile refetch
// re-renders and hides it on its own. The re-sync is idempotent and runs in small
// staggered windows; see healthkit.resyncAllHealthData.
export function AppleHealthResyncCard() {
    const dispatch = useAppDispatch();
    const profile = useAppSelector(orm.getProfile);

    if (profile == null) {
        return null;
    }

    const announcements = profile.user.casted_dismissed_announcements;
    if (
        !announcements.includes("NEEDS_APPLE_HEALTH_RESYNC") ||
        announcements.includes("RESYNCED_APPLE_HEALTH")
    ) {
        return null;
    }

    // Only resolve the native earliest-sample date once we know we'd actually show
    // the card. Older native binaries don't ship getEarliestSampleDate; without it
    // (or with no samples at all) we can't bound the resync, so don't offer it.
    const earliest = React.use(hardware.getEarliestSampleDate());
    const earliestDate =
        earliest.status === "success" ? earliest.data.earliestDate : null;

    if (earliestDate == null) {
        return null;
    }

    return (
        <habits.JournalHabitShell
            label="Re-sync Apple Health"
            habit={{
                name: "Re-sync Apple Health",
                colors: "bg-indigo-100 text-indigo-600",
                icon: {node: <ArrowPathIcon className="size-4" />},
            }}
            content={
                <>
                    <habits.TitleAndDescription
                        title="Action Required: Apple Health"
                        description="This can take a few minutes."
                    />
                    <typography.AsyncButton
                        type="button"
                        className="
                            shrink-0 rounded-md bg-indigo-600 px-2.5 py-1.5 text-xs
                            font-semibold text-white shadow-xs
                            hover:bg-indigo-500
                        "
                        onClick={async () => {
                            await healthkit.resyncAllHealthData(profile, earliestDate);
                            // Persist the flag; dismissAnnouncement refetches the
                            // profile into the store, and since this card reads the
                            // profile straight from redux (useAppSelector above) it
                            // re-renders and hides.
                            await dispatch(
                                orm.dismissAnnouncement("RESYNCED_APPLE_HEALTH"),
                            );
                            dispatch(
                                notify("Apple Health data updated.", {type: "success"}),
                            );
                        }}
                    >
                        Update
                    </typography.AsyncButton>
                </>
            }
            isComplete={null}
        />
    );
}
